import React from 'react';
import { useAppContext } from '../FirebaseAppContext';
import { HybridIcon } from '../components/HybridIcon';
import type { Car, Rental, ServiceLog } from '../models';
import './Overview.css';

interface Metric {
  key: string;
  label: string;
  value: string | number;
  icon: string;
  tone: 'accent' | 'good' | 'warn' | 'danger';
  description?: string;
}

interface ExpiringDoc {
  car: Car;
  label: string;
  date: Date;
}

interface UpcomingReturn {
  rental: Rental;
  daysAway: number;
}

interface TimelineItem {
  key: string;
  title: string;
  detail: string;
  tone: 'accent' | 'warn' | 'danger';
  icon: string;
}

function MetricCard({ metric }: { metric: Metric }) {
  return (
    <div className={`metric-card metric-card-${metric.tone}`}>
      <div className={`metric-icon metric-icon-${metric.tone}`}>
        <HybridIcon name={metric.icon} size={20} color="currentColor" />
      </div>
      <p className="metric-label">{metric.label}</p>
      <div className="metric-value">{metric.value}</div>
      {metric.description ? <div className="metric-description">{metric.description}</div> : null}
    </div>
  );
}

const formatShortDate = (date: Date | undefined) =>
  date
    ? new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)
    : '';

const formatLongDate = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);

export default function Overview() {
  const { state } = useAppContext();

  const today = React.useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const servicesByCar = React.useMemo(() => {
    const map = new Map<string, ServiceLog[]>();
    state.services.forEach((service) => {
      if (!map.has(service.carId)) {
        map.set(service.carId, []);
      }
      map.get(service.carId)?.push(service);
    });
    return map;
  }, [state.services]);

  const serviceDueCars = React.useMemo(() => {
    return state.cars.filter((car) => {
      const logs = servicesByCar.get(car.id) ?? [];
      if (!logs.length) {
        return false;
      }

      let nextKm: number | undefined;
      for (const log of logs) {
        const candidate =
          typeof log.nextServiceKm === 'number'
            ? log.nextServiceKm
            : log.mileage
              ? Math.ceil(log.mileage / 5000) * 5000
              : undefined;

        if (candidate && (!nextKm || candidate > nextKm)) {
          nextKm = candidate;
        }
      }

      return typeof nextKm === 'number' && car.mileage >= nextKm;
    });
  }, [servicesByCar, state.cars]);

  const activeRentals = React.useMemo(
    () =>
      state.rentals.filter((rental) => {
        if (!rental.endDate) {
          return true;
        }
        const end = new Date(rental.endDate);
        if (Number.isNaN(end.getTime())) {
          return true;
        }
        return end >= today;
      }),
    [state.rentals, today],
  );

  const rentalsEndingSoon = React.useMemo<UpcomingReturn[]>(() => {
    return state.rentals
      .map((rental) => {
        if (!rental.endDate) {
          return null;
        }

        const end = new Date(rental.endDate);
        if (Number.isNaN(end.getTime())) {
          return null;
        }

        const diff = Math.round((end.getTime() - today.getTime()) / 86_400_000);
        if (diff < 0 || diff > 7) {
          return null;
        }

        return { rental, daysAway: diff };
      })
      .filter((item): item is UpcomingReturn => Boolean(item));
  }, [state.rentals, today]);

  const expiringDocuments = React.useMemo<ExpiringDoc[]>(() => {
    const items: ExpiringDoc[] = [];

    const pushDoc = (car: Car, label: string, raw?: string) => {
      if (!raw) {
        return;
      }

      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) {
        return;
      }

      items.push({ car, label, date });
    };

    state.cars.forEach((car) => {
      pushDoc(car, 'Fitness expiration', car.fitnessExpirationDate);
      pushDoc(car, 'License expiration', car.licenseExpirationDate);
      pushDoc(car, 'Insurance expiration', car.insuranceExpirationDate);
    });

    const thirtyDays = 30 * 86_400_000;
    return items
      .filter(({ date }) => date >= today && date.getTime() - today.getTime() <= thirtyDays)
      .sort((left, right) => left.date.getTime() - right.date.getTime())
      .slice(0, 5);
  }, [state.cars, today]);

  const carsByStatus = React.useMemo(
    () =>
      state.cars.reduce(
        (acc, car) => {
          acc.total += 1;
          acc[car.status] += 1;
          return acc;
        },
        { total: 0, available: 0, rented: 0, maintenance: 0 },
      ),
    [state.cars],
  );

  const metrics: Metric[] = React.useMemo(
    () => [
      {
        key: 'totalCars',
        label: 'Cars in Fleet',
        value: carsByStatus.total,
        icon: 'car',
        tone: 'accent',
      },
      {
        key: 'availableCars',
        label: 'Available Cars',
        value: carsByStatus.available,
        icon: 'checkmark-circle',
        tone: 'good',
      },
      {
        key: 'activeRentals',
        label: 'Active Rentals',
        value: activeRentals.length,
        icon: 'list',
        tone: 'accent',
      },
      {
        key: 'maintenanceCars',
        label: 'In Maintenance',
        value: carsByStatus.maintenance,
        icon: 'construct',
        tone: carsByStatus.maintenance ? 'danger' : 'good',
      },
      {
        key: 'serviceDue',
        label: 'Service Due',
        value: serviceDueCars.length,
        icon: 'alert',
        tone: serviceDueCars.length ? 'warn' : 'good',
        description: serviceDueCars.length ? 'Schedule maintenance soon.' : 'All clear.',
      },
      {
        key: 'customers',
        label: 'Customers',
        value: state.customers.length,
        icon: 'people',
        tone: 'accent',
      },
    ],
    [
      activeRentals.length,
      carsByStatus.available,
      carsByStatus.maintenance,
      carsByStatus.total,
      serviceDueCars.length,
      state.customers.length,
    ],
  );

  const dayStrip = React.useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const date = new Date(today);
        date.setDate(today.getDate() + index);
        return date;
      }),
    [today],
  );

  const timeline = React.useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    rentalsEndingSoon.forEach(({ rental, daysAway }) => {
      const car = state.cars.find((entry) => entry.id === rental.carId);
      const customer = state.customers.find((entry) => entry.id === rental.customerId);
      items.push({
        key: `return-${rental.id}`,
        title: `${car?.licensePlate || 'Vehicle'} returns ${daysAway === 0 ? 'today' : `in ${daysAway} day${daysAway === 1 ? '' : 's'}`}`,
        detail: `${customer?.name || 'Unknown customer'} · ${formatShortDate(new Date(rental.endDate!))}`,
        tone: daysAway <= 1 ? 'warn' : 'accent',
        icon: 'calendar',
      });
    });

    serviceDueCars.forEach((car) => {
      items.push({
        key: `service-${car.id}`,
        title: `${car.licensePlate || `${car.make} ${car.model}`} needs service`,
        detail: `${car.mileage.toLocaleString()} km · ${car.status}`,
        tone: 'warn',
        icon: 'construct',
      });
    });

    expiringDocuments.forEach((item, index) => {
      items.push({
        key: `doc-${item.car.id}-${index}`,
        title: `${item.label} for ${item.car.licensePlate || item.car.make}`,
        detail: `Expires ${formatShortDate(item.date)}`,
        tone: 'danger',
        icon: 'shield-checkmark',
      });
    });

    return items.slice(0, 6);
  }, [expiringDocuments, rentalsEndingSoon, serviceDueCars, state.cars, state.customers]);

  return (
    <div className="overview-container">
      <div className="overview-wrapper">
        <section className="overview-hero card">
          <div className="overview-hero-copy">
            <p className="eyebrow">Today</p>
            <h1 className="overview-title">Keep the fleet moving.</h1>
            <p className="overview-subtitle">
              {formatLongDate(today)}. Track urgent returns, maintenance, and expiring documents from one screen.
            </p>
            <div className="overview-hero-pills">
              <span className="pill pill-solid">{activeRentals.length} active rentals</span>
              <span className={`pill ${serviceDueCars.length ? 'pill-warn' : 'pill-good'}`}>
                {serviceDueCars.length} service alerts
              </span>
              <span className={`pill ${expiringDocuments.length ? 'pill-danger' : 'pill-accent'}`}>
                {expiringDocuments.length} docs expiring
              </span>
            </div>
          </div>

          <div className="overview-hero-side">
            <div className="overview-hero-stat">
              <span>Ready to dispatch</span>
              <strong>{carsByStatus.available}</strong>
            </div>
            <div className="overview-hero-stat">
              <span>Returns this week</span>
              <strong>{rentalsEndingSoon.length}</strong>
            </div>
          </div>
        </section>

        <section className="overview-daystrip">
          <div className="daystrip">
            {dayStrip.map((date, index) => (
              <div key={date.toISOString()} className={`daystrip-item ${index === 0 ? 'is-active' : ''}`}>
                <strong>{new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date)}</strong>
                <span>{new Intl.DateTimeFormat(undefined, { day: 'numeric' }).format(date)}</span>
              </div>
            ))}
          </div>
        </section>

        {state.isLoading ? (
          <div className="overview-centered card">
            <div className="spinner" />
            <p className="loading-text">Loading fleet data...</p>
          </div>
        ) : (
          <>
            <div className="metrics-grid">
              {metrics.map((metric) => (
                <MetricCard key={metric.key} metric={metric} />
              ))}
            </div>

            {state.error ? (
              <div className="section-card error-card">
                <HybridIcon name="alert" size={20} color="currentColor" />
                <p className="error-text">Error loading data: {state.error}</p>
              </div>
            ) : null}

            <div className="overview-sections-grid">
              <section className="section-card section-card-featured">
                <div className="section-header">
                  <span className="section-icon section-icon-accent">
                    <HybridIcon name="sparkles" size={18} color="currentColor" />
                  </span>
                  <div>
                    <p className="eyebrow">What&apos;s Happening</p>
                    <h2 className="section-title">The next moves</h2>
                  </div>
                </div>

                {timeline.length ? (
                  <div className="overview-timeline">
                    {timeline.map((item) => (
                      <div key={item.key} className="overview-timeline-item lrow">
                        <div className={`avatar timeline-avatar timeline-avatar-${item.tone}`}>
                          <HybridIcon name={item.icon} size={18} color="currentColor" />
                        </div>
                        <div>
                          <p className="list-primary">{item.title}</p>
                          <p className="list-secondary">{item.detail}</p>
                        </div>
                        <span className={`pill ${item.tone === 'danger' ? 'pill-danger' : item.tone === 'warn' ? 'pill-warn' : 'pill-accent'}`}>
                          {item.tone === 'danger' ? 'urgent' : item.tone === 'warn' ? 'soon' : 'active'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-state">No urgent changes on the board.</p>
                )}
              </section>

              <div className="overview-section-column">
                <section className="section-card">
                  <div className="section-header">
                    <span className="section-icon section-icon-warn">
                      <HybridIcon name="construct" size={18} color="currentColor" />
                    </span>
                    <div>
                      <p className="eyebrow">Maintenance</p>
                      <h2 className="section-title">Service queue</h2>
                    </div>
                  </div>

                  {serviceDueCars.length ? (
                    serviceDueCars.map((car) => (
                      <div key={car.id} className="overview-list-row lrow">
                        <div className="car-thumb">
                          <HybridIcon name="car-sport" size={20} color="currentColor" />
                        </div>
                        <div>
                          <p className="list-primary">{car.licensePlate || `${car.make} ${car.model}`}</p>
                          <p className="list-secondary">{car.mileage.toLocaleString()} km · {car.make} {car.model}</p>
                        </div>
                        <span className="pill pill-warn">Due now</span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No cars are overdue for service.</p>
                  )}
                </section>

                <section className="section-card">
                  <div className="section-header">
                    <span className="section-icon section-icon-danger">
                      <HybridIcon name="shield-checkmark" size={18} color="currentColor" />
                    </span>
                    <div>
                      <p className="eyebrow">Compliance</p>
                      <h2 className="section-title">Document expirations</h2>
                    </div>
                  </div>

                  {expiringDocuments.length ? (
                    expiringDocuments.map((item, idx) => (
                      <div key={`${item.car.id}-${idx}`} className="overview-list-row lrow">
                        <div className="avatar timeline-avatar-danger">
                          <HybridIcon name="document-text" size={18} color="currentColor" />
                        </div>
                        <div>
                          <p className="list-primary">{item.car.licensePlate || `${item.car.make} ${item.car.model}`}</p>
                          <p className="list-secondary">{item.label} · {formatShortDate(item.date)}</p>
                        </div>
                        <span className="pill pill-danger">Due soon</span>
                      </div>
                    ))
                  ) : (
                    <p className="empty-state">No upcoming expirations within the next 30 days.</p>
                  )}
                </section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
